import React, { useContext } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router';
import { MarketplaceTile } from '@hofsync/design-system';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useChat } from '../../components/chat/ChatContext';
import { APP_REGISTRY, getAccessibleApps, type AppRegistryEntry } from './registry';
import TileViewToggle from '../../components/TileViewToggle';
import { useTileViewMode } from '../../hooks/useTileViewMode';
import './AppsPage.css';

function tileForAvailable(app: AppRegistryEntry, navigate: NavigateFunction, compact: boolean) {
  const Icon = app.icon;
  // SPA navigation: anchor stays for AT semantics + native middle-click
  // open-in-new-tab, but preventDefault + navigate routes the in-tab
  // click through React Router so the page doesn't fully reload.
  // Without this, the tile click triggers a document reload (bundle
  // re-parse + auth re-check + in-memory state loss).
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!app.route) return;
    event.preventDefault();
    navigate(app.route);
  };

  // Compact row: icon + title only. Even the editor-app tile renders as a
  // plain <a href> here — dropping the composite footer removes the nested
  // interactive that forced the card root to a <div>, so the compact row is
  // uniformly keyboard-navigable. The "Öffnen →" CTA text is redundant (the
  // whole row is the affordance) and the Editor shortcut is dropped in
  // compact (simplest, no nested interactive).
  if (compact) {
    return (
      <MarketplaceTile
        key={app.id}
        icon={<Icon size={28} />}
        title={app.title}
        href={app.route ?? '#'}
        onClick={handleClick}
        variant="available"
        compact
      />
    );
  }

  if (app.editorRoute) {
    // Two-CTA tile (#1289): the footer carries a real <a> "Öffnen →"
    // (primary CTA — native middle-click + AT link semantics) and a
    // SIBLING <button> "Editor", so no interactive element nests inside
    // another. The tile root is therefore a plain <div> (no `href`):
    // its onClick is a mouse-convenience target only — no role/tabIndex
    // — while the keyboard/AT path is the natively-focusable link and
    // button in DOM order. Both children stopPropagation so their
    // clicks don't double-fire the root handler.
    const footer = (
      <span className="apps-page__available-footer">
        <a
          className="apps-page__open-link"
          href={app.route}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (app.route) navigate(app.route);
          }}
        >
          Öffnen →
        </a>
        <button
          type="button"
          className="apps-page__editor-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(app.editorRoute!);
          }}
        >
          Editor
        </button>
      </span>
    );
    return (
      <MarketplaceTile
        key={app.id}
        icon={<Icon size={28} />}
        title={app.title}
        scope={app.description}
        footer={footer}
        onClick={handleClick}
        variant="available"
      />
    );
  }

  return (
    <MarketplaceTile
      key={app.id}
      icon={<Icon size={28} />}
      title={app.title}
      scope={app.description}
      footer="Öffnen →"
      href={app.route ?? '#'}
      onClick={handleClick}
      variant="available"
    />
  );
}

function tileForInDevelopment(
  app: AppRegistryEntry,
  onFeedback: (app: AppRegistryEntry) => void,
  compact: boolean,
) {
  const Icon = app.icon;
  // Compact row: icon + title only — the roadmap status note lives on the
  // hover tooltip, not as inline meta (Antonio, 2026-07-07); the subtitle and
  // Feedback CTA stay dropped (no nested interactive in the row). The
  // disabled a11y contract (non-tabbable <div>) is preserved by the atom.
  if (compact) {
    return (
      <MarketplaceTile
        key={app.id}
        icon={<Icon size={28} />}
        title={app.title}
        variant="disabled"
        hoverTitle={app.developmentNote ?? 'In Entwicklung'}
        compact
      />
    );
  }
  // In-dev tiles surface a "Feedback →" inline link alongside the
  // development note (#945). The link is stopPropagation'd so a click
  // doesn't bubble to MarketplaceTile's disabled root.
  const footer = (
    <span className="apps-page__indev-footer">
      <span className="apps-page__indev-note">{app.developmentNote}</span>
      {app.feedbackPrefill && (
        <button
          type="button"
          className="apps-page__feedback-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onFeedback(app);
          }}
        >
          Feedback <span aria-hidden>→</span>
        </button>
      )}
    </span>
  );
  return (
    <MarketplaceTile
      key={app.id}
      icon={<Icon size={28} />}
      title={app.title}
      scope={app.description}
      footer={footer}
      variant="disabled"
      hoverTitle={app.developmentNote ?? 'In Entwicklung'}
    />
  );
}

export default function AppsPage() {
  const { hasFlag } = useFeatureFlags();
  const navigate = useNavigate();
  const { open: openChat } = useChat();
  const { mode, setMode } = useTileViewMode();
  const compact = mode === 'compact';
  // Available apps are gated per-user by their feature flag (so a
  // half-built canary doesn't ship to every tenant). In-dev apps are
  // always shown — they're the roadmap, not a feature.
  //
  // HOF-696 D6: farm apps open on the active farm's apps × the seat's
  // permissions. Read optionally — the page's suites render it bare.
  const activeFarm = useContext(AuthContext)?.userProfile?.active_farm ?? null;
  const availableApps = getAccessibleApps(hasFlag, activeFarm);
  const inDevApps = APP_REGISTRY.filter((app) => app.status === 'in_development');
  const hasTiles = availableApps.length > 0 || inDevApps.length > 0;

  const handleFeedback = (app: AppRegistryEntry) => {
    openChat({ prefill: app.feedbackPrefill, sourceApp: app.id });
  };

  return (
    <div className="apps-page">
      {hasTiles && (
        <div className="apps-page__toolbar">
          <TileViewToggle mode={mode} onChange={setMode} />
        </div>
      )}

      {hasTiles && (
        <div className="apps-page__grid" data-view={mode}>
          {availableApps.map((app) => tileForAvailable(app, navigate, compact))}
          {inDevApps.map((app) => tileForInDevelopment(app, handleFeedback, compact))}
        </div>
      )}

      {!hasTiles && (
        <p className="apps-page__empty">Keine Apps verfügbar.</p>
      )}
    </div>
  );
}
